import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/EmployeePortal.css";

const pad = value => String(value).padStart(2, "0");
const localDate = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const todayString = () => localDate(new Date());
const plusDaysString = days => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDate(date);
};
const tomorrowAt = (hour, minute) => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(hour, minute, 0, 0);
  return `${localDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const toInputDateTime = value => value ? String(value).slice(0, 16) : "";
const formatDateTime = value => value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" }) : "-";
const formatTime = value => value ? new Date(value).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" }) : "-";
const getErrorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const bookingStatusLabels = {
  BOOKED: "Zarezerwowane",
  COMPLETED: "Odbyte",
  CANCELLED_BY_EMPLOYEE: "Anulowane przez pracownika",
  CANCELLED_BY_HR: "Anulowane przez HR",
  NO_SHOW: "Nieobecność"
};

const meetingStatusLabels = {
  SCHEDULED: "Zaplanowane",
  COMPLETED: "Zakończone",
  CANCELLED: "Anulowane"
};

const rsvpLabels = {
  NO_RESPONSE: "Brak odpowiedzi",
  YES: "Będę",
  MAYBE: "Wstępnie tak",
  NO: "Na pewno nie"
};

const emptySlot = departmentId => ({
  id: null,
  departmentId: departmentId || "",
  title: "Spotkanie z HR",
  startAt: tomorrowAt(10, 0),
  endAt: tomorrowAt(10, 30),
  location: "",
  meetingUrl: "",
  notes: "",
  active: true,
  version: null
});

const emptyAvailabilityBlock = departmentId => ({
  departmentId: departmentId || "",
  title: "Konsultacja z HR",
  startAt: tomorrowAt(10, 0),
  endAt: tomorrowAt(14, 0),
  slotDurationMinutes: 30,
  location: "",
  meetingUrl: "",
  notes: "",
  active: true
});

const emptyGroupMeeting = () => ({
  id: null,
  title: "",
  description: "",
  startAt: tomorrowAt(10, 0),
  endAt: tomorrowAt(11, 0),
  location: "",
  meetingUrl: "",
  allDepartments: false,
  departmentIds: [],
  employeeIds: [],
  version: null
});

const HrAppointmentsManager = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeMode, setActiveMode] = useState("individual");
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [dateFrom, setDateFrom] = useState(todayString());
  const [dateTo, setDateTo] = useState(plusDaysString(60));
  const [includeInactive, setIncludeInactive] = useState(false);
  const [bookingStatus, setBookingStatus] = useState("");
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [groupMeetings, setGroupMeetings] = useState([]);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showGroupMeetingModal, setShowGroupMeetingModal] = useState(false);
  const [slotForm, setSlotForm] = useState(emptySlot(""));
  const [availabilityForm, setAvailabilityForm] = useState(emptyAvailabilityBlock(""));
  const [groupMeetingForm, setGroupMeetingForm] = useState(emptyGroupMeeting());
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewSignature, setPreviewSignature] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDecision, setBookingDecision] = useState({ status: "BOOKED", managerNote: "", version: null });

  const filterParams = useMemo(() => ({
    ...(departmentId ? { departmentId: Number(departmentId) } : {}),
    dateFrom,
    dateTo
  }), [departmentId, dateFrom, dateTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [referenceResponse, slotsResponse, bookingsResponse, meetingsResponse] = await Promise.all([
        axiosPrivate.get("/hr-appointments/manager/reference-data"),
        axiosPrivate.get("/hr-appointments/manager/slots", { params: { ...filterParams, includeInactive } }),
        axiosPrivate.get("/hr-appointments/manager/bookings", { params: { ...filterParams, ...(bookingStatus ? { status: bookingStatus } : {}) } }),
        axiosPrivate.get("/hr-appointments/manager/group-meetings", { params: { dateFrom, dateTo } })
      ]);
      setDepartments(referenceResponse.data?.departments || []);
      setEmployees(referenceResponse.data?.employees || []);
      setSlots(slotsResponse.data || []);
      setBookings(bookingsResponse.data || []);
      setGroupMeetings(meetingsResponse.data || []);
    } catch (error) {
      setSlots([]);
      setBookings([]);
      setGroupMeetings([]);
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [axiosPrivate, filterParams, includeInactive, bookingStatus]);

  const openNewAvailability = () => {
    const defaultDepartment = departmentId || (departments.length === 1 ? departments[0].id : "");
    setAvailabilityForm(emptyAvailabilityBlock(defaultDepartment));
    setShowAvailabilityModal(true);
  };

  const openEditSlot = slot => {
    setSlotForm({
      id: slot.id,
      departmentId: slot.departmentId || "",
      title: slot.title || "",
      startAt: toInputDateTime(slot.startAt),
      endAt: toInputDateTime(slot.endAt),
      location: slot.location || "",
      meetingUrl: slot.meetingUrl || "",
      notes: slot.notes || "",
      active: !!slot.active,
      version: slot.version
    });
    setShowSlotModal(true);
  };

  const saveAvailabilityBlock = async () => {
    if (!availabilityForm.departmentId || !availabilityForm.title.trim() || !availabilityForm.startAt || !availabilityForm.endAt) {
      toastError("Uzupełnij dział, nazwę oraz godziny dostępności.");
      return;
    }
    if (availabilityForm.endAt <= availabilityForm.startAt) {
      toastError("Koniec dostępności musi być późniejszy niż początek.");
      return;
    }
    if (!availabilityForm.slotDurationMinutes || Number(availabilityForm.slotDurationMinutes) < 10) {
      toastError("Podaj długość pojedynczego spotkania.");
      return;
    }
    setSaving(true);
    try {
      const response = await axiosPrivate.post("/hr-appointments/manager/availability-blocks", {
        ...availabilityForm,
        departmentId: Number(availabilityForm.departmentId),
        slotDurationMinutes: Number(availabilityForm.slotDurationMinutes),
        title: availabilityForm.title.trim(),
        location: availabilityForm.location.trim() || null,
        meetingUrl: availabilityForm.meetingUrl.trim() || null,
        notes: availabilityForm.notes.trim() || null
      });
      toastSuccess(`Utworzono ${response.data?.length || 0} terminów do rezerwacji.`);
      setShowAvailabilityModal(false);
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const saveSlot = async () => {
    if (!slotForm.departmentId || !slotForm.title.trim() || !slotForm.startAt || !slotForm.endAt) {
      toastError("Uzupełnij dział, nazwę oraz godziny spotkania.");
      return;
    }
    if (slotForm.endAt <= slotForm.startAt) {
      toastError("Koniec spotkania musi być późniejszy niż początek.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...slotForm,
        departmentId: Number(slotForm.departmentId),
        title: slotForm.title.trim(),
        location: slotForm.location.trim() || null,
        meetingUrl: slotForm.meetingUrl.trim() || null,
        notes: slotForm.notes.trim() || null
      };
      await axiosPrivate.put(`/hr-appointments/manager/slots/${slotForm.id}`, payload);
      toastSuccess("Termin spotkania został zaktualizowany.");
      setShowSlotModal(false);
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteSlot = async slot => {
    if (!window.confirm(`Usunąć termin ${formatDateTime(slot.startAt)}?`)) return;
    try {
      await axiosPrivate.delete(`/hr-appointments/manager/slots/${slot.id}`);
      toastSuccess("Termin został usunięty.");
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    }
  };

  const openBooking = booking => {
    setSelectedBooking(booking);
    setBookingDecision({
      status: booking.status,
      managerNote: booking.managerNote || "",
      version: booking.version
    });
  };

  const saveBooking = async () => {
    if (!selectedBooking) return;
    setSaving(true);
    try {
      await axiosPrivate.put(`/hr-appointments/manager/bookings/${selectedBooking.id}`, bookingDecision);
      toastSuccess("Status spotkania został zaktualizowany.");
      setSelectedBooking(null);
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const groupMeetingPayload = form => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    startAt: form.startAt,
    endAt: form.endAt,
    location: form.location.trim() || null,
    meetingUrl: form.meetingUrl.trim() || null,
    allDepartments: !!form.allDepartments,
    departmentIds: form.allDepartments ? [] : form.departmentIds.map(Number),
    employeeIds: form.employeeIds.map(Number),
    version: form.version
  });

  const groupMeetingSignature = form => JSON.stringify(groupMeetingPayload(form));

  const invalidatePreview = updater => {
    setGroupMeetingForm(previous => {
      const next = typeof updater === "function" ? updater(previous) : updater;
      return next;
    });
    setAudiencePreview(null);
    setPreviewSignature("");
  };

  const openNewGroupMeeting = () => {
    setGroupMeetingForm(emptyGroupMeeting());
    setAudiencePreview(null);
    setPreviewSignature("");
    setEmployeeSearch("");
    setShowGroupMeetingModal(true);
  };

  const openEditGroupMeeting = meeting => {
    setGroupMeetingForm({
      id: meeting.id,
      title: meeting.title || "",
      description: meeting.description || "",
      startAt: toInputDateTime(meeting.startAt),
      endAt: toInputDateTime(meeting.endAt),
      location: meeting.location || "",
      meetingUrl: meeting.meetingUrl || "",
      allDepartments: !!meeting.allDepartments,
      departmentIds: meeting.departmentIds || [],
      employeeIds: meeting.employeeIds || [],
      version: meeting.version
    });
    setAudiencePreview(null);
    setPreviewSignature("");
    setEmployeeSearch("");
    setShowGroupMeetingModal(true);
  };

  const validateGroupMeetingForm = () => {
    if (!groupMeetingForm.title.trim() || !groupMeetingForm.startAt || !groupMeetingForm.endAt) {
      toastError("Uzupełnij nazwę oraz godziny spotkania.");
      return false;
    }
    if (groupMeetingForm.endAt <= groupMeetingForm.startAt) {
      toastError("Koniec spotkania musi być późniejszy niż początek.");
      return false;
    }
    if (!groupMeetingForm.allDepartments && groupMeetingForm.departmentIds.length === 0 && groupMeetingForm.employeeIds.length === 0) {
      toastError("Wybierz wszystkie działy, co najmniej jeden dział albo konkretnych pracowników.");
      return false;
    }
    return true;
  };

  const previewGroupMeeting = async () => {
    if (!validateGroupMeetingForm()) return;
    setSaving(true);
    try {
      const payload = groupMeetingPayload(groupMeetingForm);
      const response = await axiosPrivate.post("/hr-appointments/manager/group-meetings/preview", payload);
      setAudiencePreview(response.data);
      setPreviewSignature(JSON.stringify(payload));
      if (response.data?.conflictCount > 0) {
        toastSuccess(`Sprawdzono ${response.data.recipientCount} osób. Konflikt z grafikiem: ${response.data.conflictCount}.`);
      } else {
        toastSuccess(`Sprawdzono ${response.data?.recipientCount || 0} osób. Brak konfliktów z grafikiem.`);
      }
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const saveGroupMeeting = async () => {
    if (!validateGroupMeetingForm()) return;
    const payload = groupMeetingPayload(groupMeetingForm);
    if (!audiencePreview || previewSignature !== JSON.stringify(payload)) {
      toastError("Najpierw sprawdź uczestników i konflikty z grafikiem.");
      return;
    }
    setSaving(true);
    try {
      if (groupMeetingForm.id) {
        await axiosPrivate.put(`/hr-appointments/manager/group-meetings/${groupMeetingForm.id}`, payload);
        toastSuccess("Spotkanie zostało zaktualizowane, a uczestnicy otrzymali powiadomienie.");
      } else {
        await axiosPrivate.post("/hr-appointments/manager/group-meetings", payload);
        toastSuccess("Spotkanie zostało utworzone, a uczestnicy otrzymali powiadomienie.");
      }
      setShowGroupMeetingModal(false);
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const cancelGroupMeeting = async meeting => {
    if (!window.confirm(`Odwołać spotkanie „${meeting.title}”? Zaproszeni pracownicy dostaną powiadomienie.`)) return;
    try {
      await axiosPrivate.delete(`/hr-appointments/manager/group-meetings/${meeting.id}`);
      toastSuccess("Spotkanie zostało odwołane.");
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    }
  };

  const completeGroupMeeting = async meeting => {
    try {
      await axiosPrivate.post(`/hr-appointments/manager/group-meetings/${meeting.id}/complete`);
      toastSuccess("Spotkanie zostało oznaczone jako zakończone.");
      await loadData();
    } catch (error) {
      toastError(getErrorMessage(error));
    }
  };

  const toggleDepartment = id => {
    invalidatePreview(previous => ({
      ...previous,
      departmentIds: previous.departmentIds.includes(id)
        ? previous.departmentIds.filter(value => value !== id)
        : [...previous.departmentIds, id]
    }));
  };

  const toggleEmployee = id => {
    invalidatePreview(previous => ({
      ...previous,
      employeeIds: previous.employeeIds.includes(id)
        ? previous.employeeIds.filter(value => value !== id)
        : [...previous.employeeIds, id]
    }));
  };

  const filteredEmployees = useMemo(() => {
    const phrase = employeeSearch.trim().toLowerCase();
    if (!phrase) return employees;
    return employees.filter(employee =>
      `${employee.fullName || ""} ${(employee.departmentNames || []).join(" ")}`.toLowerCase().includes(phrase)
    );
  }, [employees, employeeSearch]);

  return (
    <div id="layoutSidenav_content" className="employee-portal-page">
      <ToastContainer />
      <div className="employee-portal-header shadow-sm">
        <div>
          <div className="employee-portal-eyebrow">HR</div>
          <h2>Spotkania HR</h2>
          <div className="employee-portal-subtitle">Konsultacje indywidualne, spotkania grupowe, zaproszenia i odpowiedzi pracowników.</div>
        </div>
        <div className="employee-portal-header-actions">
          {activeMode === "individual" ? <Button onClick={openNewAvailability}>Dodaj dostępność</Button> : <Button onClick={openNewGroupMeeting}>Nowe spotkanie grupowe</Button>}
          <Button variant="outline-secondary" onClick={loadData}>Odśwież</Button>
        </div>
      </div>

      <div className="container-fluid px-4 py-4">
        <div className="employee-portal-tabs mb-3">
          <Button variant={activeMode === "individual" ? "primary" : "outline-secondary"} onClick={() => setActiveMode("individual")}>Konsultacje indywidualne</Button>
          <Button variant={activeMode === "group" ? "primary" : "outline-secondary"} onClick={() => setActiveMode("group")}>Spotkania grupowe</Button>
        </div>

        {activeMode === "individual" && (
          <>
            <section className="employee-portal-card mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <Form.Label>Dział</Form.Label>
                  <Form.Select value={departmentId} onChange={event => setDepartmentId(event.target.value)}>
                    <option value="">Wszystkie dostępne działy</option>
                    {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-2"><Form.Label>Od</Form.Label><Form.Control type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></div>
                <div className="col-md-2"><Form.Label>Do</Form.Label><Form.Control type="date" min={dateFrom} value={dateTo} onChange={event => setDateTo(event.target.value)} /></div>
                <div className="col-md-3">
                  <Form.Label>Status rezerwacji</Form.Label>
                  <Form.Select value={bookingStatus} onChange={event => setBookingStatus(event.target.value)}>
                    <option value="">Wszystkie</option>
                    <option value="BOOKED">Zarezerwowane</option>
                    <option value="COMPLETED">Odbyte</option>
                    <option value="CANCELLED_BY_EMPLOYEE">Anulowane przez pracownika</option>
                    <option value="CANCELLED_BY_HR">Anulowane przez HR</option>
                    <option value="NO_SHOW">Nieobecność</option>
                  </Form.Select>
                </div>
                <div className="col-md-2"><Form.Check type="switch" label="Pokaż wyłączone" checked={includeInactive} onChange={event => setIncludeInactive(event.target.checked)} /></div>
              </div>
            </section>

            {loading ? <LoadingDataDiv /> : (
              <div className="employee-portal-grid employee-portal-grid-two">
                <section className="employee-portal-card">
                  <div className="employee-portal-card-header"><h5>Terminy do rezerwacji</h5><Button size="sm" onClick={openNewAvailability}>Dodaj blok dostępności</Button></div>
                  {slots.length === 0 ? <div className="employee-portal-empty">Brak terminów w wybranym okresie.</div> : (
                    <div className="employee-portal-list">
                      {slots.map(slot => (
                        <div className="employee-portal-list-item" key={slot.id}>
                          <div className="d-flex justify-content-between gap-2"><strong>{slot.title}</strong><span className={`employee-appointment-status ${slot.booked ? "booked" : slot.active ? "available" : "cancelled"}`}>{slot.booked ? "Zarezerwowany" : slot.active ? "Wolny" : "Wyłączony"}</span></div>
                          <span>{formatDateTime(slot.startAt)} – {formatTime(slot.endAt)}</span>
                          <small>{slot.departmentName}{slot.location ? ` · ${slot.location}` : slot.meetingUrl ? " · online" : ""}</small>
                          {slot.notes && <small>{slot.notes}</small>}
                          <div className="d-flex gap-2"><Button size="sm" variant="outline-primary" onClick={() => openEditSlot(slot)}>Edytuj</Button><Button size="sm" variant="outline-danger" onClick={() => deleteSlot(slot)} disabled={slot.booked}>Usuń</Button></div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="employee-portal-card">
                  <div className="employee-portal-card-header"><h5>Rezerwacje pracowników</h5></div>
                  {bookings.length === 0 ? <div className="employee-portal-empty">Brak rezerwacji w wybranym okresie.</div> : (
                    <div className="employee-portal-list">
                      {bookings.map(booking => (
                        <div className="employee-portal-list-item" key={booking.id}>
                          <div className="d-flex justify-content-between gap-2"><strong>{booking.employeeFullName || "Pracownik"}</strong><span className={`employee-appointment-status ${booking.status === "BOOKED" ? "booked" : booking.status === "COMPLETED" ? "completed" : "cancelled"}`}>{bookingStatusLabels[booking.status] || booking.status}</span></div>
                          <span>{formatDateTime(booking.startAt)} · {booking.title}</span>
                          <small>{booking.departmentName}{booking.location ? ` · ${booking.location}` : ""}</small>
                          <strong className="employee-appointment-subject">{booking.subject}</strong>
                          {booking.message && <small>{booking.message}</small>}
                          {booking.managerNote && <div className="employee-portal-reply"><strong>Notatka HR:</strong> {booking.managerNote}</div>}
                          <div><Button size="sm" onClick={() => openBooking(booking)}>Otwórz</Button></div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            )}
          </>
        )}

        {activeMode === "group" && (
          <>
            <section className="employee-portal-card mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-3"><Form.Label>Od</Form.Label><Form.Control type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></div>
                <div className="col-md-3"><Form.Label>Do</Form.Label><Form.Control type="date" min={dateFrom} value={dateTo} onChange={event => setDateTo(event.target.value)} /></div>
                <div className="col-md-6 d-flex justify-content-end"><Button onClick={openNewGroupMeeting}>Nowe spotkanie grupowe</Button></div>
              </div>
            </section>
            {loading ? <LoadingDataDiv /> : groupMeetings.length === 0 ? <section className="employee-portal-card"><div className="employee-portal-empty">Brak spotkań grupowych w wybranym okresie.</div></section> : (
              <div className="employee-portal-list">
                {groupMeetings.map(meeting => (
                  <section className="employee-portal-card" key={meeting.id}>
                    <div className="employee-portal-card-header">
                      <div>
                        <h5 className="mb-1">{meeting.title}</h5>
                        <small>{formatDateTime(meeting.startAt)} – {formatTime(meeting.endAt)}</small>
                      </div>
                      <span className={`employee-appointment-status ${meeting.status === "SCHEDULED" ? "booked" : meeting.status === "COMPLETED" ? "completed" : "cancelled"}`}>{meetingStatusLabels[meeting.status] || meeting.status}</span>
                    </div>
                    {meeting.description && <p className="mb-2">{meeting.description}</p>}
                    <div className="hr-meeting-summary-grid">
                      <span><strong>{meeting.recipientCount}</strong><small>zaproszonych</small></span>
                      <span><strong>{meeting.yesCount}</strong><small>będzie</small></span>
                      <span><strong>{meeting.maybeCount}</strong><small>wstępnie tak</small></span>
                      <span><strong>{meeting.noCount}</strong><small>nie będzie</small></span>
                      <span><strong>{meeting.noResponseCount}</strong><small>bez odpowiedzi</small></span>
                      <span className={meeting.conflictCount > 0 ? "hr-meeting-conflict-count" : ""}><strong>{meeting.conflictCount}</strong><small>konfliktów z grafikiem</small></span>
                    </div>
                    <details className="hr-meeting-participants mt-3">
                      <summary>Uczestnicy i odpowiedzi</summary>
                      <div className="employee-portal-list mt-2">
                        {(meeting.invitations || []).map(invitation => (
                          <div className={`employee-portal-list-item ${invitation.scheduleConflict ? "hr-meeting-conflict-row" : ""}`} key={invitation.id}>
                            <div className="d-flex justify-content-between gap-2"><strong>{invitation.employeeFullName}</strong><span>{rsvpLabels[invitation.rsvpStatus] || invitation.rsvpStatus}</span></div>
                            <small>{(invitation.departmentNames || []).join(", ") || "Brak działu"}</small>
                            {invitation.scheduleConflict && <small className="text-danger">Konflikt z grafikiem w chwili wysłania zaproszenia</small>}
                          </div>
                        ))}
                      </div>
                    </details>
                    {meeting.status === "SCHEDULED" && (
                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button size="sm" variant="outline-primary" onClick={() => openEditGroupMeeting(meeting)}>Edytuj</Button>
                        <Button size="sm" variant="outline-success" onClick={() => completeGroupMeeting(meeting)}>Oznacz jako zakończone</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => cancelGroupMeeting(meeting)}>Odwołaj</Button>
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Modal show={showAvailabilityModal} onHide={() => !saving && setShowAvailabilityModal(false)} centered size="lg" backdrop="static">
        <Modal.Header closeButton={!saving}><Modal.Title>Nowy blok dostępności HR</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted">Podaj cały okres dostępności i długość pojedynczej konsultacji. System automatycznie utworzy osobne terminy do rezerwacji.</p>
          <div className="row g-3">
            <div className="col-md-6"><Form.Label>Dział</Form.Label><Form.Select value={availabilityForm.departmentId} onChange={event => setAvailabilityForm(prev => ({ ...prev, departmentId: event.target.value }))}><option value="">-- wybierz --</option>{departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}</Form.Select></div>
            <div className="col-md-6"><Form.Label>Nazwa</Form.Label><Form.Control value={availabilityForm.title} onChange={event => setAvailabilityForm(prev => ({ ...prev, title: event.target.value }))} /></div>
            <div className="col-md-4"><Form.Label>Początek dostępności</Form.Label><Form.Control type="datetime-local" value={availabilityForm.startAt} onChange={event => setAvailabilityForm(prev => ({ ...prev, startAt: event.target.value }))} /></div>
            <div className="col-md-4"><Form.Label>Koniec dostępności</Form.Label><Form.Control type="datetime-local" value={availabilityForm.endAt} onChange={event => setAvailabilityForm(prev => ({ ...prev, endAt: event.target.value }))} /></div>
            <div className="col-md-4"><Form.Label>Długość spotkania</Form.Label><Form.Select value={availabilityForm.slotDurationMinutes} onChange={event => setAvailabilityForm(prev => ({ ...prev, slotDurationMinutes: event.target.value }))}><option value="15">15 minut</option><option value="20">20 minut</option><option value="30">30 minut</option><option value="45">45 minut</option><option value="60">60 minut</option></Form.Select></div>
            <div className="col-md-6"><Form.Label>Miejsce</Form.Label><Form.Control value={availabilityForm.location} onChange={event => setAvailabilityForm(prev => ({ ...prev, location: event.target.value }))} placeholder="np. biuro HR" /></div>
            <div className="col-md-6"><Form.Label>Link do spotkania online</Form.Label><Form.Control type="url" value={availabilityForm.meetingUrl} onChange={event => setAvailabilityForm(prev => ({ ...prev, meetingUrl: event.target.value }))} placeholder="https://..." /></div>
            <div className="col-12"><Form.Label>Informacja dla pracownika</Form.Label><Form.Control as="textarea" rows={3} value={availabilityForm.notes} onChange={event => setAvailabilityForm(prev => ({ ...prev, notes: event.target.value }))} /></div>
            <div className="col-12"><Form.Check type="switch" label="Terminy aktywne i widoczne dla pracowników" checked={availabilityForm.active} onChange={event => setAvailabilityForm(prev => ({ ...prev, active: event.target.checked }))} /></div>
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowAvailabilityModal(false)} disabled={saving}>Anuluj</Button><Button onClick={saveAvailabilityBlock} disabled={saving}>{saving ? "Tworzenie..." : "Utwórz terminy"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showSlotModal} onHide={() => !saving && setShowSlotModal(false)} centered size="lg" backdrop="static">
        <Modal.Header closeButton={!saving}><Modal.Title>Edycja terminu HR</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6"><Form.Label>Dział</Form.Label><Form.Select value={slotForm.departmentId} onChange={event => setSlotForm(prev => ({ ...prev, departmentId: event.target.value }))}><option value="">-- wybierz --</option>{departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}</Form.Select></div>
            <div className="col-md-6"><Form.Label>Nazwa</Form.Label><Form.Control value={slotForm.title} onChange={event => setSlotForm(prev => ({ ...prev, title: event.target.value }))} /></div>
            <div className="col-md-6"><Form.Label>Początek</Form.Label><Form.Control type="datetime-local" value={slotForm.startAt} onChange={event => setSlotForm(prev => ({ ...prev, startAt: event.target.value }))} /></div>
            <div className="col-md-6"><Form.Label>Koniec</Form.Label><Form.Control type="datetime-local" value={slotForm.endAt} onChange={event => setSlotForm(prev => ({ ...prev, endAt: event.target.value }))} /></div>
            <div className="col-md-6"><Form.Label>Miejsce</Form.Label><Form.Control value={slotForm.location} onChange={event => setSlotForm(prev => ({ ...prev, location: event.target.value }))} placeholder="np. biuro HR" /></div>
            <div className="col-md-6"><Form.Label>Link do spotkania online</Form.Label><Form.Control type="url" value={slotForm.meetingUrl} onChange={event => setSlotForm(prev => ({ ...prev, meetingUrl: event.target.value }))} placeholder="https://..." /></div>
            <div className="col-12"><Form.Label>Informacja dla pracownika</Form.Label><Form.Control as="textarea" rows={3} value={slotForm.notes} onChange={event => setSlotForm(prev => ({ ...prev, notes: event.target.value }))} /></div>
            <div className="col-12"><Form.Check type="switch" label="Termin aktywny i widoczny dla pracowników" checked={slotForm.active} onChange={event => setSlotForm(prev => ({ ...prev, active: event.target.checked }))} /></div>
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowSlotModal(false)} disabled={saving}>Anuluj</Button><Button onClick={saveSlot} disabled={saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showGroupMeetingModal} onHide={() => !saving && setShowGroupMeetingModal(false)} centered size="xl" backdrop="static">
        <Modal.Header closeButton={!saving}><Modal.Title>{groupMeetingForm.id ? "Edycja spotkania grupowego" : "Nowe spotkanie grupowe"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6"><Form.Label>Nazwa spotkania</Form.Label><Form.Control value={groupMeetingForm.title} onChange={event => invalidatePreview(prev => ({ ...prev, title: event.target.value }))} placeholder="np. Spotkanie organizacyjne przed sezonem" /></div>
            <div className="col-md-3"><Form.Label>Początek</Form.Label><Form.Control type="datetime-local" value={groupMeetingForm.startAt} onChange={event => invalidatePreview(prev => ({ ...prev, startAt: event.target.value }))} /></div>
            <div className="col-md-3"><Form.Label>Koniec</Form.Label><Form.Control type="datetime-local" value={groupMeetingForm.endAt} onChange={event => invalidatePreview(prev => ({ ...prev, endAt: event.target.value }))} /></div>
            <div className="col-md-6"><Form.Label>Miejsce</Form.Label><Form.Control value={groupMeetingForm.location} onChange={event => invalidatePreview(prev => ({ ...prev, location: event.target.value }))} placeholder="np. sala konferencyjna" /></div>
            <div className="col-md-6"><Form.Label>Link do spotkania online</Form.Label><Form.Control type="url" value={groupMeetingForm.meetingUrl} onChange={event => invalidatePreview(prev => ({ ...prev, meetingUrl: event.target.value }))} placeholder="https://..." /></div>
            <div className="col-12"><Form.Label>Opis / informacja dla uczestników</Form.Label><Form.Control as="textarea" rows={3} value={groupMeetingForm.description} onChange={event => invalidatePreview(prev => ({ ...prev, description: event.target.value }))} /></div>
          </div>

          <div className="hr-meeting-audience mt-4">
            <h6>Odbiorcy zaproszenia</h6>
            <Form.Check type="switch" label="Wszystkie działy" checked={groupMeetingForm.allDepartments} onChange={event => invalidatePreview(prev => ({ ...prev, allDepartments: event.target.checked, departmentIds: event.target.checked ? [] : prev.departmentIds }))} />
            {!groupMeetingForm.allDepartments && (
              <div className="hr-meeting-checkbox-grid mt-2">
                {departments.map(department => <Form.Check key={department.id} type="checkbox" label={department.name} checked={groupMeetingForm.departmentIds.includes(department.id)} onChange={() => toggleDepartment(department.id)} />)}
              </div>
            )}

            <div className="mt-3">
              <Form.Label>Dodatkowo wybrane osoby</Form.Label>
              <Form.Control className="mb-2" value={employeeSearch} onChange={event => setEmployeeSearch(event.target.value)} placeholder="Szukaj po imieniu, nazwisku lub dziale" />
              <div className="hr-meeting-employee-picker">
                {filteredEmployees.map(employee => (
                  <Form.Check
                    key={employee.id}
                    type="checkbox"
                    checked={groupMeetingForm.employeeIds.includes(employee.id)}
                    onChange={() => toggleEmployee(employee.id)}
                    label={`${employee.fullName}${employee.departmentNames?.length ? ` · ${employee.departmentNames.join(", ")}` : ""}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 mt-4">
            <Button variant="outline-primary" onClick={previewGroupMeeting} disabled={saving}>Sprawdź uczestników i konflikty z grafikiem</Button>
            {audiencePreview && <span><strong>{audiencePreview.recipientCount}</strong> zaproszonych · <strong className={audiencePreview.conflictCount > 0 ? "text-danger" : ""}>{audiencePreview.conflictCount}</strong> konfliktów</span>}
          </div>

          {audiencePreview && (
            <div className="hr-meeting-preview mt-3">
              {audiencePreview.recipients.map(recipient => (
                <div className={`hr-meeting-preview-row ${recipient.scheduleConflict ? "conflict" : "ok"}`} key={recipient.employeeId}>
                  <div>
                    <strong>{recipient.employeeFullName}</strong>
                    <small>{(recipient.departmentNames || []).join(", ") || "Brak działu"}</small>
                  </div>
                  <div className="hr-meeting-preview-result">
                    <span>{recipient.scheduleConflict ? "Konflikt z grafikiem" : "Może uczestniczyć"}</span>
                    {recipient.scheduleConflict && (recipient.conflicts || []).map(conflict => (
                      <small key={conflict.assignmentId}>{formatDateTime(conflict.startAt)} – {formatTime(conflict.endAt)} · {conflict.positionName || "zmiana"} · {conflict.scheduleName || "grafik"} ({conflict.scheduleStatus})</small>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowGroupMeetingModal(false)} disabled={saving}>Anuluj</Button><Button onClick={saveGroupMeeting} disabled={saving || !audiencePreview || previewSignature !== groupMeetingSignature(groupMeetingForm)}>{saving ? "Zapisywanie..." : groupMeetingForm.id ? "Zapisz i powiadom" : "Utwórz i wyślij zaproszenia"}</Button></Modal.Footer>
      </Modal>

      <Modal show={!!selectedBooking} onHide={() => !saving && setSelectedBooking(null)} centered>
        <Modal.Header closeButton={!saving}><Modal.Title>Obsługa spotkania</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedBooking && <div className="mb-3"><strong>{selectedBooking.employeeFullName}</strong><br />{formatDateTime(selectedBooking.startAt)}<br />{selectedBooking.subject}</div>}
          <Form.Group className="mb-3"><Form.Label>Status</Form.Label><Form.Select value={bookingDecision.status} onChange={event => setBookingDecision(prev => ({ ...prev, status: event.target.value }))}><option value="BOOKED">Zarezerwowane</option><option value="COMPLETED">Odbyte</option><option value="CANCELLED_BY_HR">Anulowane przez HR</option><option value="NO_SHOW">Pracownik nie przyszedł</option>{selectedBooking?.status === "CANCELLED_BY_EMPLOYEE" && <option value="CANCELLED_BY_EMPLOYEE">Anulowane przez pracownika</option>}</Form.Select></Form.Group>
          <Form.Group><Form.Label>Notatka / informacja dla pracownika</Form.Label><Form.Control as="textarea" rows={4} value={bookingDecision.managerNote} onChange={event => setBookingDecision(prev => ({ ...prev, managerNote: event.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setSelectedBooking(null)} disabled={saving}>Zamknij</Button><Button onClick={saveBooking} disabled={saving || bookingDecision.status === "CANCELLED_BY_EMPLOYEE"}>Zapisz</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default HrAppointmentsManager;
